/**
 * usePageAccess Hook
 * Provides access to page data and organization page access management
 */

import { useAtom } from "jotai";
import { useMemo, useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { pagesQueryAtom, orgPagesQueryAtom } from "@/store/adminDataStore";
import { adminAPI } from "@/services";
import type { IPage, IOrgPages } from "@/services";
import { invalidateWithTimeout } from "@/utils/queryUtils";

export interface UsePageAccessReturn {
  /** All pages */
  pages: IPage[];
  /** Organization page mappings */
  orgPages: IOrgPages[];
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
  /** Refetch function */
  refetch: () => void;
  /** Get pages for a specific organization */
  getOrgPages: (orgId: number) => IPage[];
  /** Check if organization has access to page */
  hasAccess: (orgId: number, pageId: number) => boolean;
  /** Grant organization access to page */
  addAccess: (orgId: number, pageId: number) => Promise<boolean>;
  /** Revoke organization access to page */
  removeAccess: (orgId: number, pageId: number) => Promise<boolean>;
  /** Updating state */
  isUpdating: boolean;
  /** Update error */
  updateError: Error | null;
}

export function usePageAccess(): UsePageAccessReturn {
  const [pagesQuery] = useAtom(pagesQueryAtom);
  const [orgPagesQuery] = useAtom(orgPagesQueryAtom);
  const queryClient = useQueryClient();
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<Error | null>(null);

  // All pages
  const pages = useMemo(() => pagesQuery.data ?? [], [pagesQuery.data]);

  // Organization page mappings
  const orgPages = useMemo(
    () => orgPagesQuery.data ?? [],
    [orgPagesQuery.data],
  );

  // Map of org ID to page IDs for fast lookup
  const orgAccessMap = useMemo(() => {
    const map = new Map<number, Set<number>>();
    orgPages.forEach((mapping) => {
      const pageIds = new Set(mapping.pages.map((p) => p.id));
      map.set(mapping.organization_id, pageIds);
    });
    return map;
  }, [orgPages]);

  // Get pages for a specific organization
  const getOrgPages = useCallback(
    (orgId: number): IPage[] => {
      const mapping = orgPages.find((m) => m.organization_id === orgId);
      return mapping?.pages ?? [];
    },
    [orgPages],
  );

  // Check if organization has access to page
  const hasAccess = useCallback(
    (orgId: number, pageId: number): boolean => {
      const pageIds = orgAccessMap.get(orgId);
      return pageIds?.has(pageId) ?? false;
    },
    [orgAccessMap],
  );

  // Add access mutation
  const addAccess = useCallback(
    async (orgId: number, pageId: number): Promise<boolean> => {
      setIsUpdating(true);
      setUpdateError(null);

      try {
        await adminAPI.addPageAccess({
          organization_id: orgId,
          page_id: pageId,
        });
        await invalidateWithTimeout(queryClient, ["orgPages"]);
        return true;
      } catch (error) {
        setUpdateError(error as Error);
        return false;
      } finally {
        setIsUpdating(false);
      }
    },
    [queryClient],
  );

  // Remove access mutation
  const removeAccess = useCallback(
    async (orgId: number, pageId: number): Promise<boolean> => {
      setIsUpdating(true);
      setUpdateError(null);

      try {
        await adminAPI.removePageAccess({
          organization_id: orgId,
          page_id: pageId,
        });
        await invalidateWithTimeout(queryClient, ["orgPages"]);
        return true;
      } catch (error) {
        setUpdateError(error as Error);
        return false;
      } finally {
        setIsUpdating(false);
      }
    },
    [queryClient],
  );

  // Invalidate queries - marks as stale and refetches active queries
  const refetch = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["pages"],
        refetchType: "active",
      }),
      queryClient.invalidateQueries({
        queryKey: ["orgPages"],
        refetchType: "active",
      }),
    ]);
  }, [queryClient]);

  return {
    pages,
    orgPages,
    isLoading: pagesQuery.isLoading || orgPagesQuery.isLoading,
    error: pagesQuery.error ?? orgPagesQuery.error ?? null,
    refetch,
    getOrgPages,
    hasAccess,
    addAccess,
    removeAccess,
    isUpdating,
    updateError,
  };
}
